import React, { useState } from 'react';
import styled from '@emotion/styled';
import { alignSelf, space } from 'styled-system';

import TelField from 'common/components/Form/TelField';
import TextareaField from 'common/components/Form/TextareaField';
import TextField from 'common/components/Form/TextField';
import InfoButton from 'common/components/InfoButton';
import { useOpenDrawer } from 'common/hooks/drawer';
import type { Translate } from 'common/providers/LocaleProvider';
import type {
  ErrorMessagesMap,
  FieldChangeTriggerHandler,
  FormData,
  HasErrorMap,
} from 'common/types/form';
import { isValidMarketplaceOrderNumber } from 'common/utils/form/validation';
import ChannelList from '../components/ChannelSelection/ChannelList';
import { FieldName, FieldType } from '../components/enums';
import { HelpContentDrawer } from '../components/Fields/HelpContentDrawer';
import Images from '../components/Fields/Images';
import OrderNumberDrawer from '../components/Fields/OrderNumberDrawer';
import ProductNumberDrawer from '../components/Fields/ProductNumberDrawer';
import { SignupLink } from '../components/FormTypes/SignupLink';
import type { ContactFormFieldSlice, ContactFormGroup } from '../components/types';
import { isReasonLegalConcern } from '../utils/reasons';

interface GetFormGroup {
  translate: Translate;
  slice: ContactFormFieldSlice;
  formData: FormData<FieldName>;
  hasErrorMap: HasErrorMap<FieldName>;
  errorMessagesMap: ErrorMessagesMap<FieldName>;
  handleInputChange: FieldChangeTriggerHandler<FieldName>;
  isLoggedIn: boolean;
  orderFetchError: boolean;
  productFetchError: boolean;
  reason: string;
  setOrderDrawerError: () => void;
  setProductDrawerError: () => void;
  openItemNumberHelpDrawer: () => void;
  isSellerContactMode: boolean;
}

interface UseFormGroups extends Omit<
  GetFormGroup,
  | 'slice'
  | 'orderFetchError'
  | 'productFetchError'
  | 'setOrderDrawerError'
  | 'setProductDrawerError'
  | 'openItemNumberHelpDrawer'
> {
  slices: ContactFormFieldSlice[];
}

interface GetDrawerFieldAttributes extends Pick<GetFormGroup, 'translate'> {
  orderId?: string;
  optionalLabel: string;
  fieldName: FieldName;
  isLoggedIn: boolean;
  orderFetchError?: boolean;
  productFetchError?: boolean;
  reason: string;
  setOrderDrawerError?: () => void;
  setProductDrawerError?: () => void;
  isSellerContactMode?: boolean;
}

const FieldInfoButton = styled(InfoButton)`
  ${space};
  ${alignSelf};
`;

const infoButtonStyleProps = {
  alignSelf: 'flex-start',
  p: 'sp_6',
};

const fieldLabels: Record<FieldName, string> = {
  [FieldName.FirstName]: 'h24_first_name',
  [FieldName.LastName]: 'h24_last_name',
  [FieldName.Phone]: 'h24_phone_number',
  [FieldName.EmailAddress]: 'h24_email',
  [FieldName.OrderNumber]: 'h24_order_number',
  [FieldName.ProductName]: 'h24_product_name',
  [FieldName.Comment]: 'h24_comment',
  [FieldName.Images]: 'h24_contact_form_add_file',
  [FieldName.Channel]: 'h24_contact_form_channel',
  [FieldName.OrderId]: '',
};

const getComponentForSlice = ({ fieldType, fieldName }: ContactFormFieldSlice) => {
  if (fieldName === FieldName.Images) return Images;
  if (fieldName === FieldName.Phone) return TelField;
  if (fieldType === FieldType.Textarea) return TextareaField;
  if (fieldName === FieldName.Channel) return ChannelList;

  return TextField;
};

const getDrawerFieldAttributes = ({
  orderId,
  fieldName,
  optionalLabel,
  translate,
  setOrderDrawerError,
  setProductDrawerError,
  reason,
  isSellerContactMode,
}: GetDrawerFieldAttributes) => {
  if (fieldName === FieldName.OrderNumber) {
    return {
      Component: OrderNumberDrawer,
      setDrawerError: setOrderDrawerError,
      reason,
      label: `${translate('h24_select_order')}${optionalLabel}`,
    };
  }

  if (fieldName === FieldName.ProductName) {
    return {
      orderId,
      Component: ProductNumberDrawer,
      setDrawerError: setProductDrawerError,
      reason,
      label: `${translate('h24_select_product')}${optionalLabel}`,
      isSellerContactMode,
    };
  }

  return null;
};

// eslint-disable-next-line complexity
const getFormGroup = ({
  slice,
  formData,
  hasErrorMap,
  errorMessagesMap,
  isLoggedIn,
  translate,
  handleInputChange,
  orderFetchError,
  productFetchError,
  openItemNumberHelpDrawer,
  ...restProps
}: GetFormGroup): ContactFormGroup => {
  const { fieldName, required } = slice;
  const optionalLabel = required ? '' : ` (${translate('h24_optional')})`;
  const commonAttributes = {
    name: fieldName,
    label: `${translate(fieldLabels[fieldName])}${optionalLabel}`,
    required,
    hasError: hasErrorMap.get(fieldName),
    errors: errorMessagesMap.get(fieldName),
    value: formData.get(fieldName),
    onChange: handleInputChange,
    readOnly: fieldName === FieldName.EmailAddress && isLoggedIn,
    ...(fieldName === FieldName.Comment &&
      isReasonLegalConcern(restProps.reason) && {
        helpText: translate('h24_contact_form_comment_legal_concerns_help_text'),
      }),
  };

  if (fieldName === FieldName.OrderNumber && !orderFetchError && isLoggedIn) {
    Object.assign(
      commonAttributes,
      getDrawerFieldAttributes({
        fieldName,
        isLoggedIn,
        optionalLabel,
        translate,
        ...restProps,
      })
    );
  }

  if (fieldName === FieldName.OrderNumber && !isLoggedIn) {
    Object.assign(commonAttributes, {
      helpText: translate('h24_contact_form_order_number_help_text'),
    });
  }

  if (fieldName === FieldName.ProductName) {
    const shouldShowProductDrawer = required && !productFetchError && isLoggedIn;

    Object.assign(
      commonAttributes,
      shouldShowProductDrawer
        ? {
            ...getDrawerFieldAttributes({
              orderId: formData.get(FieldName.OrderId) ?? '',
              fieldName,
              isLoggedIn,
              optionalLabel,
              translate,
              ...restProps,
            }),
          }
        : {
            helpText: translate('h24_contact_form_item_number_help_text'),
            rightSlot: (
              <FieldInfoButton
                {...infoButtonStyleProps}
                ariaLabel={translate('h24_contact_form_item_number_drawer_title')}
                onClick={openItemNumberHelpDrawer}
              />
            ),
          }
    );
  }

  if (fieldName === FieldName.Channel) {
    Object.assign(commonAttributes, {
      telFieldProps: {
        label: translate(fieldLabels[FieldName.Phone]),
        name: FieldName.Phone,
        value: formData.get(FieldName.Phone),
        info: translate('h24_channel_step_phone_field_info_message'),
        onChange: handleInputChange,
      },
      shouldHideCallbackOption: isValidMarketplaceOrderNumber(formData.get(FieldName.OrderNumber)),
    });
  }

  if (fieldName === FieldName.LastName && !isReasonLegalConcern(restProps.reason)) {
    Object.assign(commonAttributes, {
      bottomSlot: <SignupLink isLoggedIn={isLoggedIn} reason={restProps.reason} />,
    });
  }

  return {
    Component: getComponentForSlice(slice),
    ...commonAttributes,
  };
};

export const useFormGroups = ({ slices, ...restProps }: UseFormGroups) => {
  const [orderFetchError, setOrderFetchError] = useState(false);
  const [productFetchError, setProductFetchError] = useState(false);
  const openItemNumberHelpDrawer = useOpenDrawer({
    title: '',
    content: <HelpContentDrawer />,
  });

  return slices.map(slice =>
    getFormGroup({
      slice,
      orderFetchError,
      productFetchError,
      openItemNumberHelpDrawer,
      setOrderDrawerError: () => setOrderFetchError(true),
      setProductDrawerError: () => setProductFetchError(true),
      ...restProps,
    })
  );
};
